"use client";

import { usePathname } from "next/navigation";
import { Suspense, useEffect, useRef } from "react";
import {
  captureClientEvent,
  captureClientException,
  capturePageleave,
  capturePageview,
  flushClientTelemetry,
} from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { getLoadErrorRecoveryKind } from "@/lib/load-error-recovery";
import { scheduleLoadErrorRecovery } from "@/lib/load-error-recovery.client";
import { createClientLogger } from "@/lib/logging/client";
import {
  type FailedResourceTag,
  getCriticalNextAssetError,
} from "@/lib/telemetry/client-error-policy";
import {
  buildErrorRuntimeContext,
  isNativeCapacitorRuntime,
} from "@/lib/telemetry/runtime";
import { AcquisitionProvider } from "./acquisition-provider";

const logger = createClientLogger("providers/client-telemetry");

/**
 * Installs the client-side safety net for everything React error boundaries
 * cannot see, and the manual pageview pipeline the Capacitor WebView needs.
 *
 * Without this, two whole classes of failure are invisible in production:
 * unhandled promise rejections (every failed fetch, every await outside a
 * try/catch) and errors thrown outside the React render phase. On native both
 * are the *only* error signal, because posthog-js exception autocapture is
 * deliberately disabled there.
 */
export function ClientTelemetryProvider() {
  return (
    <>
      <GlobalErrorReporter />
      {/*
       * `usePathname` is a dynamic client hook. Under `cacheComponents` an
       * unwrapped one at the root makes every page uncacheable and fails the
       * prerender with CLIENT_HOOK_DYNAMIC. The boundary is safe here because
       * it wraps a null-rendering leaf, not `children` — a layout must never
       * suspend over its children.
       */}
      <Suspense fallback={null}>
        <RouteAnalyticsReporter />
        <AcquisitionProvider />
      </Suspense>
    </>
  );
}

/** Errors repeat — a render loop can fire the same rejection every frame. */
const REPORT_DEDUPE_WINDOW_MS = 10_000;
const MAX_REPORTS_PER_PAGE = 25;

function GlobalErrorReporter() {
  useEffect(() => {
    const recentSignatures = new Map<string, number>();
    let reportCount = 0;
    const manuallyCaptureExceptions = isNativeCapacitorRuntime();

    const shouldReport = (signature: string) => {
      const now = Date.now();

      for (const [key, at] of recentSignatures) {
        if (now - at > REPORT_DEDUPE_WINDOW_MS) recentSignatures.delete(key);
      }

      if (recentSignatures.has(signature)) return false;
      if (reportCount >= MAX_REPORTS_PER_PAGE) return false;

      recentSignatures.set(signature, now);
      reportCount += 1;
      return true;
    };

    const report = (
      error: unknown,
      kind: "unhandled_rejection" | "uncaught_error" | "resource_error",
      extra?: Record<string, unknown>,
    ) => {
      const asError =
        error instanceof Error
          ? error
          : new Error(
              typeof error === "string"
                ? error
                : `Non-error thrown: ${String(error)}`,
            );

      const signature = `${kind}:${asError.name}:${asError.message?.slice(0, 200)}`;
      if (!shouldReport(signature)) return;

      const properties = {
        source: "global_error_handler",
        error_kind: kind,
        error_name: asError.name,
        error_message: asError.message?.slice(0, 500),
        has_stack: Boolean(asError.stack),
        ...buildErrorRuntimeContext(),
        ...extra,
      };

      captureClientException(asError, properties);
      captureClientEvent(ANALYTICS_EVENTS.CLIENT_UNHANDLED_ERROR, properties);
      logger.error(asError, { error_kind: kind });
    };

    const recover = (error: unknown) => {
      const asError =
        error instanceof Error
          ? error
          : new Error(typeof error === "string" ? error : String(error));
      scheduleLoadErrorRecovery(
        asError,
        getLoadErrorRecoveryKind(asError),
        flushClientTelemetry,
      );
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      recover(event.reason);
      if (manuallyCaptureExceptions) {
        report(event.reason, "unhandled_rejection");
      }
    };

    const onError = (event: ErrorEvent) => {
      const error = event.error ?? event.message;
      recover(error);
      if (manuallyCaptureExceptions) {
        report(error, "uncaught_error", {
          script_source: event.filename?.split("?", 1)[0],
          line_number: event.lineno,
          column_number: event.colno,
        });
      }
    };

    /**
     * Capture-phase listener: failed resources do not bubble to
     * `window.onerror`. Only first-party Next.js assets are fatal here; media
     * and vendor scripts already have their own fallback/retry paths.
     */
    const onResourceError = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || target === (window as unknown as HTMLElement)) return;

      const tag = target.tagName?.toLowerCase() as
        | FailedResourceTag
        | undefined;
      if (!tag || !["img", "script", "link", "video", "audio"].includes(tag)) {
        return;
      }

      const url =
        (target as HTMLImageElement | HTMLScriptElement).src ||
        (target as HTMLLinkElement).href;

      const error = getCriticalNextAssetError({
        tag,
        url,
        origin: window.location.origin,
      });
      if (!error) return;

      // A failed first-party chunk means the open document is from an older
      // deployment than the assets on the server. The reload below is the fix,
      // so this is recorded as a diagnostic event rather than an exception:
      // captureException here created a recurring error-tracking issue for
      // loads that recovered themselves.
      captureClientEvent(ANALYTICS_EVENTS.CLIENT_ASSET_LOAD_FAILED, {
        source: "global_error_handler",
        error_kind: "resource_error",
        error_name: error.name,
        error_message: error.message.slice(0, 500),
        resource_tag: tag,
        resource_url: url?.split("?", 1)[0],
        ...buildErrorRuntimeContext(),
      });
      logger.warn(error, { error_kind: "resource_error" });
      recover(error);
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onError);
    window.addEventListener("error", onResourceError, true);

    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onError);
      window.removeEventListener("error", onResourceError, true);
    };
  }, []);

  return null;
}

function RouteAnalyticsReporter() {
  const pathname = usePathname();
  const previousRef = useRef<{ pathname: string; enteredAt: number } | null>(
    null,
  );

  useEffect(() => {
    // posthog-js already captures pageviews/pageleaves on web. Native gets no
    // document loads at all, so it captures them here instead.
    if (!isNativeCapacitorRuntime()) return;
    if (!pathname) return;

    const previous = previousRef.current;
    if (previous) {
      capturePageleave({
        previous_pathname: previous.pathname,
        time_on_page_ms: Date.now() - previous.enteredAt,
      });
    }

    previousRef.current = { pathname, enteredAt: Date.now() };
    capturePageview({ navigation_kind: previous ? "client" : "initial" });
  }, [pathname]);

  useEffect(() => {
    if (!isNativeCapacitorRuntime()) return;

    // A backgrounded app is the native equivalent of closing the tab; without
    // this every native session would look like it ended mid-screen.
    const onHidden = () => {
      if (document.visibilityState !== "hidden") return;
      const previous = previousRef.current;
      if (!previous) return;

      capturePageleave({
        previous_pathname: previous.pathname,
        time_on_page_ms: Date.now() - previous.enteredAt,
        reason: "app_backgrounded",
      });
    };

    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, []);

  return null;
}
